// WHAT THE REVIEWER IS ACCEPTING — the read-only verdict panel for ONE combined FE export.
//
// The CIO's instruction (2026-08-11): *"the CSV to be uploaded will be ONE file that contains ALL
// of the performance level data (for portfolios, portfolio holdings, benchmarks, benchmark
// constituents, ARC, AFI & Investment Association). Make sure the front end works with this as I
// note at the moment it gives a dropdown."*
//
// So the file is no longer hand-classified. It is classified by
// `src/features/investment-reporting` and the RESULT IS PUT ON SCREEN BEFORE ANYTHING IS
// COMMITTED. This component is that screen. It is PURE: props in, JSX out, no network, no state,
// no upload. Everything that can write lives in CombinedCsvDropZone.
//
// WHY EACH BLOCK IS HERE
//
//   * THE BASIS STATEMENT IS THE FIRST THING ON THE PAGE. Line 2 of every FE Performance Table
//     carries the pricing spread, the price range and the currency — the basis on which every
//     number in the file was struck. A reviewer accepting a file is accepting THAT, so it is
//     shown at the top, verbatim as well as parsed, not folded into a tooltip.
//
//   * THE TIE CHECK IS THE INTEGRITY SIGNAL, AND IT IS NEVER APPROXIMATED. `CBAM {N} Benchmark`
//     IS the equal-weight mean of the nine peer houses at level N — the file carries both sides,
//     so the check is free. When a house is absent the mean is NOT SHOWN AS AN EIGHT-HOUSE MEAN.
//     It renders as an explicit gap. This mirrors the CellValue never-fabricate discipline
//     (CellValue.tsx:42): a missing input produces a stated absence, NEVER a number that looks
//     like an answer. On the real 2026-07 export the /8 mean was out by 0.066pp at level I,
//     0.078pp at II and 0.074pp at III — three times the 0.02pp tolerance, and it would have
//     read as a plausible number on screen.
//
//   * THE UNCLASSIFIED RESIDUE IS LISTED BY NAME, not counted. A count invites "only one, fine".
//     The name is what lets a reviewer recognise `Pounds Sterling in GB` as FE's currency
//     baseline rather than an instrument — the row that must NEVER be bound to the CASH
//     pseudo-fund, because that mints a holding with a fabricated return.
//
//   * FINDINGS READ AS ENGLISH AND STATE THE CONSEQUENCE. "composite_reconcile_failed" is not a
//     sentence. The classifier already writes the sentence; this renders it, with the arithmetic
//     consequence beside it so the reviewer knows what accepting it would have cost.
//
//   * A DE-DUPLICATION IS AN EDIT, SO IT GETS ITS OWN PANEL. The classifier collapses rows that
//     are identical in BOTH name and value (the three byte-identical Vanguard US Government Bond
//     lines are one row FE wrote three times). That is the only kind of collapse that cannot lose
//     information — but it is still a change to what will be landed, so it is shown as its own
//     block naming the row, the value, every line number and which lines were dropped. It is NOT
//     folded into the warnings list, where it could be collapsed behind "Show all". The other
//     case — one name, two different values — is a REFUSAL and renders in the refusal panel,
//     because choosing between them silently is the worst thing this surface could do.
//
//   * REFUSALS AND WARNINGS SAY DIFFERENT THINGS AND MUST LOOK DIFFERENT. A refusal means the data
//     is wrong (the nine constituents were all present and their mean did NOT match the published
//     benchmark — one of the two is wrong and we cannot tell which). A warning means something
//     could not be VERIFIED (constituents missing, so that level is accepted as published rather
//     than checked). The file is still usable in the second case, and the UI must not imply it is not.

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  FileSpreadsheet,
  Info,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
} from "lucide-react";
import { toneText } from "./labels";
import type {
  ClassifiedRow,
  CompositeLevelCheck,
  DedupeReport,
  FeCombinedClassification,
  FeProvenance,
  FeRowClass,
  Refusal,
  Warning,
} from "@/features/investment-reporting/types";

// ---------------------------------------------------------------------------
// Formatting. Percent values arrive as percent already (-1.15 means -1.15%).
// ---------------------------------------------------------------------------
export function formatPct(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(digits)}%`;
}

export function formatPp(v: number | null | undefined, digits = 3): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}pp`;
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// The seven buckets, in the order a human thinks about them: CBAM's own books first, then what
// they are measured against, then the wider comparators, then the constituents, then the residue.
const CLASS_ORDER: FeRowClass[] = [
  "cbam_book",
  "cbam_benchmark",
  "peer_constituent",
  "external_comparator",
  "ia_sector",
  "holding",
  "unclassified",
];

const CLASS_LABEL: Record<FeRowClass, string> = {
  cbam_book: "CBAM books (P1–P5, R1–R5, MM)",
  cbam_benchmark: "CBAM benchmarks (I–V)",
  peer_constituent: "Peer constituents (9 houses × 5 levels)",
  external_comparator: "External comparators (AFI, ARC)",
  ia_sector: "IA sectors",
  holding: "Holdings (individual funds)",
  unclassified: "Unclassified",
};

// What a COMPLETE export looks like. These are structural, not observed: 11 books, 5 benchmark
// rungs, 9×5 peer cells, 3 AFI + 5 ARC. Holdings and IA sectors legitimately vary between pulls,
// so they carry no expectation and are shown as a plain count.
const CLASS_EXPECTED: Partial<Record<FeRowClass, number>> = {
  cbam_book: 11,
  cbam_benchmark: 5,
  peer_constituent: 45,
  external_comparator: 8,
};

// ---------------------------------------------------------------------------
// THE BASIS STATEMENT. The provenance line, prominent — a reviewer should see what they are
// accepting before they see a single number.
// ---------------------------------------------------------------------------
export function BasisStatement({
  provenance,
  fileName,
  byteSize,
  sha256,
  rowCount,
}: {
  provenance: FeProvenance;
  fileName?: string | null;
  byteSize?: number | null;
  sha256?: string | null;
  rowCount?: number | null;
}) {
  const p = provenance;
  // An incomplete basis is worth seeing at a glance: it is the part of the record that makes the
  // numbers attributable at all.
  const incomplete = !p?.pricingSpread || !p?.currency || !p?.windowStart || !p?.windowEnd;

  return (
    <Card className={cn("p-4 space-y-3", incomplete ? "border-amber-500/50" : "border-primary/40")}>
      <div className="flex items-start gap-2">
        <FileSpreadsheet className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h4 className="font-heading text-sm font-medium text-foreground">
            The basis these numbers were struck on
          </h4>
          <p className="font-body text-[11px] text-muted-foreground">
            Line 2 of the export. This is the provenance statement — accepting the file is
            accepting this.
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5 font-body text-sm">
        <dt className="text-muted-foreground">Pricing spread</dt>
        <dd className={cn("text-foreground", !p?.pricingSpread && "text-amber-600 dark:text-amber-400")}>
          {p?.pricingSpread ?? "Not stated in the file"}
        </dd>

        <dt className="text-muted-foreground">Price range</dt>
        <dd className={cn("text-foreground", !p?.priceRangeRaw && "text-amber-600 dark:text-amber-400")}>
          {p?.priceRangeRaw ?? "Not stated in the file"}
          {p?.windowStart && p?.windowEnd && (
            <span className="text-muted-foreground text-xs">
              {" "}
              — <span className="font-mono">{p.windowStart}</span> →{" "}
              <span className="font-mono">{p.windowEnd}</span>
            </span>
          )}
        </dd>

        <dt className="text-muted-foreground">Currency</dt>
        <dd className={cn("text-foreground", !p?.currency && "text-amber-600 dark:text-amber-400")}>
          {p?.currency ?? "Not stated in the file"}
          {p?.currencyCode && <span className="text-muted-foreground text-xs"> ({p.currencyCode})</span>}
        </dd>

        <dt className="text-muted-foreground">Figures are</dt>
        <dd className="text-foreground">{p?.metricLabel ?? "—"}</dd>

        <dt className="text-muted-foreground">File</dt>
        <dd className="text-foreground break-all">
          {fileName ?? "—"}
          <span className="text-muted-foreground text-xs">
            {byteSize != null ? ` · ${formatBytes(byteSize)}` : ""}
            {rowCount != null ? ` · ${rowCount} data rows` : ""}
          </span>
        </dd>

        {sha256 && (
          <>
            <dt className="text-muted-foreground">sha256</dt>
            <dd className="text-foreground font-mono text-xs break-all" title={sha256}>
              {sha256}
            </dd>
          </>
        )}
      </dl>

      {/* The raw line, verbatim. The parsed fields above are a convenience; THIS is the audit
          string, and it is what gets stored. */}
      {p?.basisRaw && (
        <p className="font-mono text-[11px] text-muted-foreground bg-muted/50 rounded px-2 py-1.5 break-words">
          {p.basisRaw}
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Counts per class, against what a complete export should carry.
// ---------------------------------------------------------------------------
function ClassCounts({ classification }: { classification: FeCombinedClassification }) {
  const counts = classification.counts;
  const dedupe = classification.dedupe;
  const removed = dedupe?.rowsRemoved ?? 0;
  return (
    <Card className="p-4 space-y-2">
      <h4 className="font-heading text-sm font-medium text-foreground">What is in the file</h4>
      <div className="overflow-x-auto">
        <table className="w-full font-body text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs">
              <th className="text-left font-normal py-1">Class</th>
              <th className="text-right font-normal py-1 w-20">Found</th>
              <th className="text-right font-normal py-1 w-24">Expected</th>
            </tr>
          </thead>
          <tbody>
            {CLASS_ORDER.map((k) => {
              const found = counts?.[k] ?? 0;
              const expected = CLASS_EXPECTED[k];
              const short = expected != null && found !== expected;
              return (
                <tr key={k} className="border-t">
                  <td className="py-1.5 text-foreground">{CLASS_LABEL[k]}</td>
                  <td
                    className={cn(
                      "py-1.5 text-right tabular-nums",
                      short ? toneText("bad") : "text-foreground",
                    )}
                  >
                    {found}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {expected != null ? expected : "—"}
                  </td>
                </tr>
              );
            })}
            {/* The arithmetic of the de-duplication, on screen, so the total reconciles to the
                file without anyone having to take it on trust. Shown only when it happened. */}
            {removed > 0 && (
              <>
                <tr className="border-t-2">
                  <td className="py-1.5 text-muted-foreground">Data rows read from the file</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {dedupe.rowsRead}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">—</td>
                </tr>
                <tr className="border-t">
                  <td className="py-1.5 text-muted-foreground">
                    Identical copies removed{" "}
                    <span className="text-[11px]">(same name, same value)</span>
                  </td>
                  <td className={cn("py-1.5 text-right tabular-nums", toneText("attention"))}>
                    −{removed}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">—</td>
                </tr>
              </>
            )}
            <tr className={removed > 0 ? "border-t" : "border-t-2"}>
              <td className="py-1.5 text-foreground font-medium">
                {removed > 0 ? "Rows that would be landed" : "Total data rows"}
              </td>
              <td className="py-1.5 text-right tabular-nums text-foreground font-medium">
                {counts?.total ?? 0}
              </td>
              <td className="py-1.5 text-right text-muted-foreground">—</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="font-body text-[11px] text-muted-foreground">
        Counted as ROWS, not distinct names. Rows are only ever merged when they are identical in
        BOTH name and value — one real export writes the same Vanguard US Government Bond line
        three times at −1.09, and there is exactly one such fund in the estate. Two rows sharing a
        name but disagreeing on the value are never merged: that is a conflict, and it refuses the
        file rather than choosing a winner.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// WHAT THE DE-DUPLICATION DID. An edit to the file's contents gets said out loud.
// ---------------------------------------------------------------------------
function DedupeCard({ dedupe }: { dedupe: DedupeReport }) {
  const groups = dedupe?.collapsed ?? [];
  // ⚠️ TWO PASSES, TWO SECTIONS. The card must render when EITHER found something: a file can be
  // free of repeated names and still land a fund twice under two spellings, and that was the case
  // the old card could not show — it keyed its whole existence on `collapsed`.
  const identity = dedupe?.identityCollapsed ?? [];
  if (groups.length === 0 && identity.length === 0) return null;

  const nameRemoved = groups.reduce((n, g) => n + g.removedLines.length, 0);
  const identityRemoved = identity.reduce((n, g) => n + g.removedLines.length, 0);

  return (
    <Card className="p-4 space-y-2 border-amber-500/40">
      <div className="flex items-start gap-2">
        <Copy className={cn("h-4 w-4 mt-0.5 shrink-0", toneText("attention"))} aria-hidden />
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-medium text-foreground">
            Duplicate rows removed ({dedupe.rowsRemoved} of {dedupe.rowsRead})
          </h4>
          {/*
            ⚠️ THE HEADLINE COUNT IS THE TOTAL ACROSS BOTH PASSES, and the split is stated here
            rather than left to be inferred. Reading a single "N removed" over a counts table that
            reconciles to rowsRead - N invites "the file is now duplicate-free", which is a claim
            neither pass can make on its own: duplicates are found by REPEATED NAME and by RESOLVED
            FUND IDENTITY, and a third kind — one fund the roster does not know at all — is not a
            dedupe finding but an unmatched holding, reported separately.
          */}
          <p className="font-body text-[11px] text-muted-foreground">
            Two different checks ran.{" "}
            {nameRemoved > 0 && (
              <>
                <span className="text-foreground">{nameRemoved}</span> row
                {nameRemoved === 1 ? "" : "s"} repeated a NAME already in the file at the same
                value.{" "}
              </>
            )}
            {identityRemoved > 0 && (
              <>
                <span className="text-foreground">{identityRemoved}</span> row
                {identityRemoved === 1 ? "" : "s"} carried a DIFFERENT name that resolved to a fund
                already in the file — the check below the table, which a name comparison cannot
                make.{" "}
              </>
            )}
            In both cases the first was kept and the copies dropped; nothing was lost, because every
            copy said the same thing. Rows that disagree on the value are never merged either way —
            that refuses the file instead.
          </p>
        </div>
      </div>

      {groups.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full font-body text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs">
              <th className="text-left font-normal py-1">Row</th>
              <th className="text-right font-normal py-1 w-24">Value</th>
              <th className="text-right font-normal py-1 w-16">Copies</th>
              <th className="text-left font-normal py-1 pl-3 w-56">Lines</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={`${g.name}-${g.keptLine}`} className="border-t align-top">
                <td className="py-1.5 text-foreground break-words">{g.name}</td>
                <td className="py-1.5 text-right tabular-nums text-foreground">
                  {g.value != null ? (
                    formatPct(g.value)
                  ) : (
                    <span className="font-body text-xs italic text-muted-foreground">no value</span>
                  )}
                </td>
                <td className="py-1.5 text-right tabular-nums text-foreground">{g.count}</td>
                <td className="py-1.5 pl-3 font-body text-xs text-muted-foreground">
                  kept <span className="tabular-nums text-foreground">{g.keptLine}</span>, removed{" "}
                  <span className="tabular-nums">{g.removedLines.join(", ")}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {identity.length > 0 && (
        <div className="space-y-1.5">
          <h5 className="font-heading text-xs font-medium text-foreground">
            One fund, written more than one way ({identity.length})
          </h5>
          <p className="font-body text-[11px] text-muted-foreground">
            These names DIFFER, so the name check above could not see them — but each set resolves
            to a single fund in the estate register at the same value, so they are one holding, not
            two, and would otherwise have landed twice. A price return and a total return of one
            fund are not duplicates and are never merged.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead>
                <tr className="text-muted-foreground text-xs">
                  <th className="text-left font-normal py-1">Names that resolved to one fund</th>
                  <th className="text-right font-normal py-1 w-24">Value</th>
                  <th className="text-left font-normal py-1 pl-3 w-56">Lines</th>
                </tr>
              </thead>
              <tbody>
                {identity.map((g) => (
                  <tr key={`${g.fundId}-${g.totalReturn}-${g.keptLine}`} className="border-t align-top">
                    <td className="py-1.5 text-foreground break-words">
                      {g.names.map((n, i) => (
                        <span key={n} className="break-words">
                          {i > 0 && (
                            <span className="text-muted-foreground"> = </span>
                          )}
                          <span className={i === 0 ? "text-foreground" : "text-muted-foreground"}>
                            {n}
                          </span>
                        </span>
                      ))}
                      <span className="block font-body text-[11px] text-muted-foreground">
                        {g.isin ?? g.fundId}
                        {g.totalReturn ? " · total return" : " · price return"}
                      </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-foreground">
                      {g.value != null ? (
                        formatPct(g.value)
                      ) : (
                        <span className="font-body text-xs italic text-muted-foreground">
                          no value
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pl-3 font-body text-xs text-muted-foreground">
                      kept <span className="tabular-nums text-foreground">{g.keptLine}</span>,
                      removed <span className="tabular-nums">{g.removedLines.join(", ")}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="font-body text-[11px] text-muted-foreground">
        The file is uploaded byte-for-byte as it was dropped — the stored bytes and their sha256
        are the audit record and are never rewritten. The collapse applies to what is READ from
        those bytes, and the destination keys a row on (source, entity, metric, as-of date), so an
        identical row cannot land twice in any case.{" "}
        <span className="text-foreground">
          That key is on the FE name, not the fund — which is why the second check above matters:
          two spellings are two entity refs and the destination would not have stopped them.
        </span>
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// THE TIE CHECK. Never an eight-house mean.
// ---------------------------------------------------------------------------
function CompositeTieTable({
  composite,
  houseLabel,
}: {
  composite: CompositeLevelCheck[];
  houseLabel: (h: string) => string;
}) {
  const anyGap = (composite ?? []).some((c) => c.verdict !== "tie");
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-start gap-2">
        {anyGap ? (
          <ShieldAlert className={cn("h-4 w-4 mt-0.5 shrink-0", toneText("bad"))} aria-hidden />
        ) : (
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
        )}
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-medium text-foreground">
            Benchmark reconciles by construction
          </h4>
          <p className="font-body text-[11px] text-muted-foreground">
            Each CBAM benchmark IS the equal-weight mean of the nine peer houses at that level. The
            file carries both sides, so this checks itself.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full font-body text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs">
              <th className="text-left font-normal py-1">Level</th>
              <th className="text-right font-normal py-1">Houses</th>
              <th className="text-right font-normal py-1">Mean of nine</th>
              <th className="text-right font-normal py-1">Published</th>
              <th className="text-right font-normal py-1">Difference</th>
              <th className="text-left font-normal py-1 pl-3">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {(composite ?? []).map((c) => {
              const evaluated = c.verdict !== "not_evaluated";
              return (
                <tr key={c.level} className="border-t align-top">
                  <td className="py-1.5 text-foreground">{c.level}</td>
                  <td
                    className={cn(
                      "py-1.5 text-right tabular-nums",
                      c.present < c.expected ? toneText("bad") : "text-foreground",
                    )}
                  >
                    {c.present}/{c.expected}
                  </td>

                  {/* NEVER-FABRICATE. When a house is absent there is no mean — there is a gap,
                      and it says so. An eight-house mean here would be a plausible-looking
                      number that is simply not the quantity named in the column header. */}
                  <td className="py-1.5 text-right tabular-nums">
                    {c.mean != null ? (
                      <span className="text-foreground">{formatPct(c.mean)}</span>
                    ) : (
                      <span className="font-body text-xs italic text-muted-foreground">
                        Not computed
                      </span>
                    )}
                  </td>

                  <td className="py-1.5 text-right tabular-nums text-foreground">
                    {c.published != null ? (
                      formatPct(c.published)
                    ) : (
                      <span className="font-body text-xs italic text-muted-foreground">Absent</span>
                    )}
                  </td>

                  <td
                    className={cn(
                      "py-1.5 text-right tabular-nums",
                      c.verdict === "tie" ? "text-primary" : toneText("bad"),
                    )}
                  >
                    {c.diff != null ? formatPp(c.diff) : "—"}
                  </td>

                  <td className="py-1.5 pl-3">
                    {c.verdict === "tie" && (
                      <span className="inline-flex items-center gap-1 text-primary text-xs">
                        <CheckCircle2 className="h-3 w-3" aria-hidden /> ties (±{c.tolerance}pp)
                      </span>
                    )}
                    {c.verdict === "gap" && (
                      <span className={cn("inline-flex items-center gap-1 text-xs", toneText("bad"))}>
                        <ShieldX className="h-3 w-3" aria-hidden /> does not tie
                      </span>
                    )}
                    {!evaluated && (
                      <span className="font-body text-xs text-muted-foreground">
                        Cannot be checked —{" "}
                        {(c.missingHouses ?? []).map(houseLabel).join(", ") || "a house is absent"}{" "}
                        {(c.missingHouses ?? []).length === 1 ? "is" : "are"} not in this file
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The residue, BY NAME.
// ---------------------------------------------------------------------------
function UnclassifiedList({ rows }: { rows: ClassifiedRow[] }) {
  const residue = (rows ?? []).filter((r) => r.klass === "unclassified");
  if (residue.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
          <p className="font-body text-sm text-foreground">
            Every row in the file was classified.{" "}
            <span className="text-muted-foreground">Nothing was left over.</span>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-2">
      <h4 className="font-heading text-sm font-medium text-foreground">
        Rows nothing could be made of ({residue.length})
      </h4>
      <p className="font-body text-[11px] text-muted-foreground">
        Listed by name, not counted — the name is the only thing that lets you recognise what a
        leftover row actually is.
      </p>
      <ul className="space-y-1">
        {residue.map((r) => (
          <li key={`${r.line}-${r.index}`} className="flex items-start gap-2 font-body text-sm">
            <span className="text-muted-foreground text-xs tabular-nums shrink-0 pt-0.5">
              line {r.line}
            </span>
            <span className="min-w-0">
              <span className="text-foreground break-words">{r.rawName}</span>
              <span className="text-muted-foreground text-xs">
                {" "}
                · {r.value != null ? formatPct(r.value) : "no value"}
              </span>
              {r.unclassifiedReason === "fe_currency_baseline" && (
                <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                  This is FE's currency baseline, not an instrument. It is deliberately left
                  unbound — treating it as the CASH pseudo-fund would create a holding with a
                  return that nobody reported.
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Refusals — the wall, in English, with the consequence.
// ---------------------------------------------------------------------------
export function RefusalPanel({ refusals }: { refusals: Refusal[] }) {
  if (!refusals?.length) return null;
  return (
    <Card className="p-4 space-y-2 border-destructive/50 bg-destructive/5">
      <div className="flex items-start gap-2">
        <ShieldX className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
        <div>
          <h4 className="font-heading text-sm font-medium text-destructive">
            This file cannot be used
          </h4>
          <p className="font-body text-[11px] text-muted-foreground">
            Nothing has been uploaded. Fix the export and drop it again — retrying costs nothing.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {refusals.map((r, i) => (
          <li key={`${r.code}-${i}`} className="font-body text-sm text-foreground">
            {r.message}
            <span className="block font-mono text-[10px] text-muted-foreground mt-0.5">{r.code}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Warnings — real, but not a wall. Collapsed past a handful so they cannot bury the refusals.
// ---------------------------------------------------------------------------
function WarningList({ warnings }: { warnings: Warning[] }) {
  const [open, setOpen] = useState(false);
  const list = warnings ?? [];
  if (list.length === 0) return null;
  const shown = open ? list : list.slice(0, 4);

  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-start gap-2">
        <AlertTriangle
          className={cn("h-4 w-4 mt-0.5 shrink-0", toneText("attention"))}
          aria-hidden
        />
        <div>
          <h4 className="font-heading text-sm font-medium text-foreground">
            Worth knowing ({list.length})
          </h4>
          <p className="font-body text-[11px] text-muted-foreground">
            None of these stop the upload. They are the things a reviewer would want to have been
            told.
          </p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {shown.map((w, i) => (
          <li key={`${w.code}-${i}`} className="flex items-start gap-1.5">
            <Info className="h-3 w-3 mt-1 shrink-0 text-muted-foreground" aria-hidden />
            <span className="font-body text-sm text-foreground">
              {w.message}
              <span className="block font-mono text-[10px] text-muted-foreground">{w.code}</span>
            </span>
          </li>
        ))}
      </ul>
      {list.length > 4 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <>
              <ChevronDown className="h-3 w-3 mr-1" aria-hidden /> Show fewer
            </>
          ) : (
            <>
              <ChevronRight className="h-3 w-3 mr-1" aria-hidden /> Show all {list.length}
            </>
          )}
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The whole review.
// ---------------------------------------------------------------------------
export function CombinedIntakeReview({
  classification,
  fileName,
  byteSize,
  sha256,
  houseLabel,
}: {
  classification: FeCombinedClassification;
  fileName?: string | null;
  byteSize?: number | null;
  sha256?: string | null;
  /** Maps a PeerHouseId to a human label. Injected so this component knows no estate vocabulary. */
  houseLabel: (h: string) => string;
}) {
  const c = classification;
  return (
    <div className="space-y-3">
      {/* Provenance FIRST — before any number. */}
      <BasisStatement
        provenance={c.provenance}
        fileName={fileName}
        byteSize={byteSize}
        sha256={sha256}
        rowCount={c.counts?.total}
      />

      {/* Then the wall, if there is one. */}
      <RefusalPanel refusals={c.refusals} />

      <ClassCounts classification={c} />
      <DedupeCard dedupe={c.dedupe} />
      <CompositeTieTable composite={c.composite} houseLabel={houseLabel} />
      <UnclassifiedList rows={c.rows} />
      <WarningList warnings={c.warnings} />
    </div>
  );
}
